
import { defineComponent, onMounted, ref, watchEffect, computed } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { GET_ORDER_DETAIL, RESEND_EMAIL, GET_ORDER_STATUS } from '../graphql/Queries';
import { DELETE_ORDER_PRODUCT, UPDATE_ORDER_PRODUCT, UPDATE_ORDER_STATUS } from '../graphql/Mutations';
import { useRoute } from 'vue-router';
import { setPageHistory } from '@/core/helpers/toolbar';
import Table from '../../../components/Table/Table.vue';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import InnerLoader from '../../../components/InnerLoader.vue';
import { Select } from '@/components/input-elements';
import { asideTheme } from '@/core/helpers/config';
import Axios from '@/core/services/Http';
import AddOrderProducts from './AddOrderProducts.vue';
export default defineComponent({
    name: 'order',
    components: { Table, InnerLoader, Select, AddOrderProducts },

    setup() {
        const route = useRoute();
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(false);
        const orders = ref([]) as Record<any, any>;
        const customer = ref({}) as Record<any, any>;
        const orderTotals = ref({}) as Record<any, any>;
        const system_locale = ref();
        const sendMailLoader = ref(false);
        const sapResendLoader = ref(false);
        const order_status: any = ref([]);
        const statusList = ref([]) as Record<any, any>;
        const orderStatusList = ref([]) as Record<any, any>;
        const defaultImage = ref(store?.state?.AuthModule?.default_image);
        const syncingQuantity = ref(false);
        const addOrderProductsRef = ref();

        const canEditOrderProducts = computed(() => [1, 2].includes(Number(orders.value?.order_status_id)));

        const columns = computed(() => {
            const base: Array<Record<string, any>> = [
                {
                    label: '',
                    key: 'image'
                },
                {
                    label: 'message.PRODUCT',
                    key: 'description'
                },
                {
                    label: 'message.QUANTITY',
                    key: 'quantity',
                    width: true
                },
                {
                    label: 'message.PRICE',
                    key: 'price',
                    textAlignment: 'end'
                },
                {
                    label: 'message.TOTAL',
                    key: 'total',
                    textAlignment: 'end'
                }
            ];

            if (canEditOrderProducts.value) {
                base.push({
                    label: ' ',
                    key: 'actions',
                    textAlignment: 'center',
                    width: true
                });
            }

            return base;
        });

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            statusList.value = [];
            orderStatusList.value.forEach((element, index) => {
                if (element.id == 1 || element.id == 5 || element.id == 6 || element.id == 10) {
                    statusList.value.push({
                        label: JSON.parse(element?.status)[system_locale.value]?.name,
                        value: element.id
                    });
                }
            });
        });

        const me = computed(() => {
            return store.getters.getUser;
        });

        const companyLogo = name => {
            return generateAvatar(name);
        };

        const parseRemoteJson = (value: unknown) => {
            let current: any = value;

            for (let i = 0; i < 3; i++) {
                if (typeof current !== 'string') {
                    break;
                }

                const trimmed = current.trim();
                if (!trimmed) {
                    return '';
                }

                try {
                    current = JSON.parse(trimmed);
                } catch {
                    break;
                }
            }

            return current;
        };

        const isOrderSendFailed = computed(() => {
            const hasNoEconomicId = orders.value?.economic_order_id == null || orders.value?.economic_order_id === '';
            const hasNoEconomicNumber = orders.value?.economic_order_number == null || orders.value?.economic_order_number === '';
            return hasNoEconomicId && hasNoEconomicNumber;
        });

        const formattedRemoteResponse = computed(() => {
            const response = orders.value?.remote_response;
            if (!response) {
                return '';
            }

            const parsed = parseRemoteJson(response);
            const errorInfo = parseRemoteJson(parsed?.ErrorInformation || parsed?.errorInformation);

            const message =
                errorInfo?.message ||
                errorInfo?.Message ||
                parsed?.message ||
                parsed?.Message ||
                (typeof parsed === 'string' ? parsed.match(/"message"\s*:\s*"([^"]+)"/i)?.[1] : null);

            if (typeof message === 'string' && message.trim()) {
                return message;
            }

            return typeof parsed === 'string' ? parsed : '';
        });

        const resolveOrderStatusStep = (statusId: number) => {
            const stepByStatusId: Record<number, number> = {
                2: 1, // Order Received
                1: 2, // Order In Progress
                10: 3, // Shipment In Progress
                6: 4 // Order Shipped
            };
            if (stepByStatusId[statusId] != null) {
                return stepByStatusId[statusId];
            }

            let statusName = '';
            const statusRecord =
                orderStatusList.value.find(status => Number(status.id) === statusId) || orders.value?.order_status;
            try {
                const parsed = typeof statusRecord?.status === 'string' ? JSON.parse(statusRecord.status) : null;
                statusName = parsed?.[system_locale.value]?.name || parsed?.en?.name || '';
            } catch {
                statusName = '';
            }

            const normalized = String(statusName).trim().toLowerCase();
            const received = String(i18n.t('message.ORDER_RECEIVED')).trim().toLowerCase();
            const inProgress = String(i18n.t('message.ORDER_IN_PROGRESS')).trim().toLowerCase();
            const shipment = String(i18n.t('message.SHIPMENT_IN_PROGRESS')).trim().toLowerCase();
            const shipped = String(i18n.t('message.ORDER_SHIPPED')).trim().toLowerCase();

            if (normalized === shipped || normalized.includes('shipped')) return 4;
            if (normalized === shipment || normalized.includes('shipment')) return 3;
            if (normalized === inProgress || normalized.includes('progress')) return 2;
            if (normalized === received || normalized.includes('received')) return 1;
            return 0;
        };

        const orderStatusStepActive = computed(() => {
            const statusId = Number(orders.value?.order_status_id ?? orders.value?.order_status?.id);
            return resolveOrderStatusStep(Number.isFinite(statusId) ? statusId : 0);
        });

        const updateStatus = async (selectedStatusId?: number) => {
            const nextStatusId = Number(selectedStatusId ?? order_status.value);
            if (!Number.isFinite(nextStatusId) || nextStatusId <= 0) {
                return;
            }
            order_status.value = nextStatusId;
            loader.value = true;
            await Apollo.mutate({
                mutation: UPDATE_ORDER_STATUS,
                variables: { id: orders.value.id, order_status_id: nextStatusId },
                update: () => {
                    const matchedStatus = orderStatusList.value.find(status => Number(status.id) === nextStatusId);
                    orders.value = {
                        ...orders.value,
                        order_status_id: nextStatusId,
                        order_status: matchedStatus || orders.value.order_status
                    };
                    loader.value = false;
                    Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                }
            }).catch(() => {
                loader.value = false;
            });
        };

        const orderDetails = uuid => {
            loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_DETAIL,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    uuid: uuid
                }
            }).subscribe(({ data, errors }) => {
                if (errors || !data?.order_detail) {
                    loader.value = false;
                    if (!data?.order_detail) {
                        return;
                    }
                }
                const detail = data.order_detail;
                orders.value = {
                    ...detail,
                    order_products: (detail?.order_products || []).map(product => ({
                        ...product,
                        quantity: Number(product.quantity) || 1,
                        _savedQuantity: Number(product.quantity) || 1
                    }))
                };
                customer.value = detail?.customer;
                orderTotals.value = {};
                if (orders.value.order_total.length > 0) {
                    orders.value.order_total.forEach(element => {
                        orderTotals.value[element.code] = {
                            title: element.title,
                            value: element.value
                        };
                    });
                }
                loader.value = false;
            });
        };

        const orderStatus = () => {
            // loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_STATUS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only'
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                if (data?.orders_status.length > 0) {
                    orderStatusList.value = JSON.parse(data?.orders_status);
                }

                // loader.value = false;
            });
        };

        const resendMsg = order_id => {
            try {
                sendMailLoader.value = true;
                Apollo.watchQuery({
                    query: RESEND_EMAIL,
                    errorPolicy: 'all',
                    fetchPolicy: 'network-only',
                    nextFetchPolicy: 'cache-only',
                    variables: {
                        order_id: order_id
                    }
                }).subscribe(({ errors }) => {
                    if (errors) {
                        sendMailLoader.value = false;
                    }
                    sendMailLoader.value = false;
                    Notify.success(`${i18n.t('message.MESSAGE_SENT')}`);
                });
            } catch (e) {
                sendMailLoader.value = false;
                Notify.error(i18n.t('message.SOMETHING_WENT_WRONG'));
            }
        };

        const resendOrderEconomic = async order_id => {
            sapResendLoader.value = true;
            try {
                const response = await Axios.post('/fortnox/orders/' + order_id);
                const payload = response?.data?.data ?? response?.data ?? {};
                const orderNumber = payload?.economic_order_number || payload?.order_number;
                const economicOrderId = payload?.economic_order_id;

                if (orderNumber || economicOrderId) {
                    orders.value = {
                        ...orders.value,
                        economic_order_number: orderNumber || orders.value?.economic_order_number,
                        economic_order_id: economicOrderId ?? orders.value?.economic_order_id ?? orderNumber
                    };
                }

                Notify.success(i18n.t('message.FORTNOX_ORDER_RESEND_SUCCESSFULLY'));
                orderDetails(route.params.uuid);
            } catch ($e) {
                const error = $e as any;
                Notify.error(error?.response?.data?.message || error?.message || i18n.t('message.SOMETHING_WENT_WRONG'));
            } finally {
                sapResendLoader.value = false;
            }
        };

        const clearPageHistory = () => {
            setPageHistory({});
        };

        const updateOrderProductQuantity = async (item, value) => {
            if (syncingQuantity.value || loader.value || !canEditOrderProducts.value) {
                return;
            }

            const nextQty = Number(value);
            const savedQty = Number(item?._savedQuantity);
            if (!Number.isFinite(nextQty) || nextQty < 1) {
                syncingQuantity.value = true;
                item.quantity = savedQty || 1;
                syncingQuantity.value = false;
                return;
            }
            if (nextQty === savedQty) {
                return;
            }

            loader.value = true;
            try {
                await Apollo.mutate({
                    mutation: UPDATE_ORDER_PRODUCT,
                    variables: {
                        order_id: Number(orders.value?.id),
                        product_id: Number(item.product_id),
                        quantity: nextQty
                    }
                });
                Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                orderDetails(route.params.uuid);
            } catch ($e) {
                syncingQuantity.value = true;
                item.quantity = savedQty;
                syncingQuantity.value = false;
                loader.value = false;
            }
        };

        const changeOrderProductQuantity = (item, value) => {
            const nextQty = Math.floor(Number(value));
            if (!Number.isFinite(nextQty) || nextQty < 1) {
                return;
            }
            item.quantity = nextQty;
            updateOrderProductQuantity(item, nextQty);
        };

        const deleteOrderProduct = item => {
            if (!canEditOrderProducts.value) {
                return;
            }

            if ((orders.value?.order_products || []).length <= 1) {
                Notify.error('This order must keep at least one product.');
                return;
            }

            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_ORDER_PRODUCT,
                        variables: {
                            order_id: Number(orders.value?.id),
                            product_id: Number(item.product_id)
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                    orderDetails(route.params.uuid);
                })
                .catch(() => {
                    loader.value = false;
                });
        };
        const openAddProducts = () => {
            addOrderProductsRef.value?.show(Number(customer.value?.id), Number(orders.value?.id));
        };

        const refreshOrder = () => {
            orderDetails(route.params.uuid);
        };

        onMounted(() => {
            orderDetails(route.params.uuid);
            orderStatus();
        });

        return {
            orders,
            addOrderProductsRef,
            openAddProducts,
            refreshOrder,
            formattedRemoteResponse,
            me,
            customer,
            orderTotals,
            system_locale,
            columns,
            canEditOrderProducts,
            changeOrderProductQuantity,
            updateOrderProductQuantity,
            deleteOrderProduct,
            loader,
            clearPageHistory,
            companyLogo,
            i18n,
            asideTheme,
            resendMsg,
            resendOrderEconomic,
            sendMailLoader,
            sapResendLoader,
            isOrderSendFailed,
            updateStatus,
            orderStatusStepActive,
            statusList,
            order_status,
            defaultImage
        };
    }
});
